import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { SoftwareOptions } from '@shared/models/enums/enums';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { ApplianceDetailsUtils } from '@modules/appliance-details/utils/appliance-details-utils';
import { OrderQuery } from '@modules/order/state/order-query';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { RxTemplate } from '@shared/rx-templates/rx-template.model';
import { ISectionVisibilityService } from '@shared/models/section-visibility';
import { ProcedureMap } from '@shared/models/procedure-map';

export interface RxIntuitiveApplianceFieldsVisibility {
	procedureType: boolean;
	applianceType: boolean;
	upperLowerCard: boolean;
}

@Injectable({ providedIn: 'root' })
export class RxIntuitiveApplianceVisibilityService implements ISectionVisibilityService<RxIntuitiveApplianceFieldsVisibility> {
	private readonly isSwoEnabled$: Observable<boolean> = this.softwareOptionsService
		.hasCompanySoftwareOption$(SoftwareOptions.EnableApplianceDetails)
		.pipe(shareReplay({ bufferSize: 1, refCount: true }));

	private readonly isProcedureAppliance$: Observable<boolean> = this.orderQuery.procedureMap$.pipe(
		map(procedureMap => !!procedureMap && ApplianceDetailsUtils.isApplianceDetailsEnabled(procedureMap.ProcedureId)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly fields$: Observable<RxIntuitiveApplianceFieldsVisibility> = combineLatest([
		this.orderQuery.procedureMap$,
		this.isSwoEnabled$,
		this.rxTemplateService.template$
	]).pipe(
		map(([procedureMap, swoEnabled, template]) =>
			RxIntuitiveApplianceVisibilityService.fieldsFromContext(procedureMap, swoEnabled, template)
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly isSectionVisible$: Observable<boolean> = combineLatest([
		this.isProcedureAppliance$,
		this.fields$.pipe(map(f => RxIntuitiveApplianceVisibilityService.isSectionNonEmpty(f)))
	]).pipe(
		map(([isAppliance, nonEmpty]) => isAppliance && nonEmpty),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	static fieldsFromContext(
		procedureMap: ProcedureMap | null,
		swoEnabled: boolean,
		template: Readonly<RxTemplate>
	): RxIntuitiveApplianceFieldsVisibility {
		return {
			procedureType: !template.ui.appliance?.items.procedureType?.hidden,
			applianceType:
				swoEnabled && !!procedureMap && ApplianceDetailsUtils.isApplianceTypeVisible(procedureMap.ProcedureId, procedureMap.TypeId),
			upperLowerCard: swoEnabled && !template.ui.appliance?.items.upperLowerCard.hidden
		};
	}

	static isSectionNonEmpty(f: RxIntuitiveApplianceFieldsVisibility): boolean {
		return f.procedureType || f.applianceType || f.upperLowerCard;
	}

	constructor(
		private readonly orderQuery: OrderQuery,
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly rxTemplateService: RxTemplateService
	) {}

	fieldsFor$(
		targetTemplate: Readonly<RxTemplate>,
		targetProcedureMap: ProcedureMap | null
	): Observable<RxIntuitiveApplianceFieldsVisibility> {
		// No procedure map or non-appliance procedure → all fields hidden.
		if (!targetProcedureMap || !ApplianceDetailsUtils.isApplianceDetailsEnabled(targetProcedureMap.ProcedureId)) {
			return of({ procedureType: false, applianceType: false, upperLowerCard: false });
		}

		return this.isSwoEnabled$.pipe(
			take(1),
			map(swoEnabled => RxIntuitiveApplianceVisibilityService.fieldsFromContext(targetProcedureMap, swoEnabled, targetTemplate))
		);
	}
}
